import { estimateClockOffsetMs } from '@badger-sentinel/protocol';

/**
 * Client-side clock correction (§8.2).
 *
 * All deadlines arrive as absolute epoch ms on the *server* clock. A player whose
 * laptop is 40 s fast would otherwise watch the orders window close early, which is
 * a support ticket every session.
 *
 * The estimator keeps a short history and reports the offset from the sample with
 * the lowest round-trip time, rather than averaging. Averaging lets one queued or
 * retransmitted packet drag the estimate; the fastest round trip is the one least
 * distorted by path asymmetry, which is the same reasoning NTP uses.
 */
export interface ClockSample {
  readonly offsetMs: number;
  readonly roundTripMs: number;
  readonly atMs: number;
}

export class ClockEstimator {
  private readonly samples: ClockSample[] = [];

  constructor(private readonly historySize = 8) {}

  /** Folds one ping/pong round trip into the estimate and returns the sample. */
  observe(args: {
    clientSentAtMs: number;
    serverTimeMs: number;
    clientReceivedAtMs: number;
  }): ClockSample {
    const { offsetMs, roundTripMs } = estimateClockOffsetMs(args);
    const sample: ClockSample = { offsetMs, roundTripMs, atMs: args.clientReceivedAtMs };

    this.samples.push(sample);
    if (this.samples.length > this.historySize) this.samples.shift();
    return sample;
  }

  /** Best current estimate. 0 until the first pong arrives. */
  get offsetMs(): number {
    return this.best?.offsetMs ?? 0;
  }

  get roundTripMs(): number | null {
    return this.best?.roundTripMs ?? null;
  }

  get sampleCount(): number {
    return this.samples.length;
  }

  /** Server time now, per the current estimate. */
  serverNow(clientNowMs: number): number {
    return clientNowMs + this.offsetMs;
  }

  /** Milliseconds remaining until an absolute server-clock deadline. */
  msUntil(deadlineServerMs: number, clientNowMs: number): number {
    return deadlineServerMs - this.serverNow(clientNowMs);
  }

  reset(): void {
    this.samples.length = 0;
  }

  private get best(): ClockSample | undefined {
    return this.samples.reduce<ClockSample | undefined>(
      (best, sample) =>
        best === undefined || sample.roundTripMs < best.roundTripMs ? sample : best,
      undefined,
    );
  }
}
